import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float, Stars } from '@react-three/drei';
import * as THREE from 'three';

// ====== Styled Components (Responsive) ======
const MainSectionContainer = styled.section`
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%);
  position: relative;
  overflow: hidden;
`;

const CanvasContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const ContentOverlay = styled.div`
  position: relative;
  z-index: 10;
  width: 100%;
  height: auto;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 0;

  @media (max-width: 1024px) {
    padding: 30px 0;
  }
  @media (max-width: 768px) {
    padding: 20px 10px;
    align-items: flex-start;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 5vw, 3.5rem);
  color: #ffffff;
  margin-bottom: 20px;
  text-align: center;
  font-family: 'Roboto', sans-serif;
  text-shadow: 0 0 20px rgba(255, 107, 53, 0.5);
`;

const SectionSubtitle = styled(motion.p)`
  font-size: clamp(1rem, 2.5vw, 1.2rem);
  color: #cccccc;
  margin-bottom: 50px;
  text-align: center;
  max-width: 900px;
  line-height: 1.8;
  font-family: 'Roboto', sans-serif;
  padding: 0 10px;
`;

const FeaturesContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 25px;
  width: 100%;
  max-width: 1200px;
  padding: 0 10px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const FeatureCard = styled(motion.div)`
  background: rgba(26, 26, 26, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 30px 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(45deg, rgba(255, 107, 53, 0.1), rgba(74, 144, 226, 0.1));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-10px) scale(1.02);
    border-color: rgba(255, 107, 53, 0.3);
    box-shadow: 0 20px 40px rgba(255, 107, 53, 0.2);

    &::before {
      opacity: 1;
    }
  }

  @media (max-width: 480px) {
    padding: 20px 15px;
  }
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 15px;
  text-align: center;
  background: linear-gradient(45deg, #FF6B35, #4a90e2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const FeatureTitle = styled.h3`
  font-size: 1.2rem;
  color: #ffffff;
  margin-bottom: 10px;
  text-align: center;
  font-family: 'Roboto', sans-serif;
  font-weight: 600;
`;

const FeatureDescription = styled.p`
  font-size: 0.95rem;
  color: #cccccc;
  line-height: 1.6;
  text-align: center;
  font-family: 'Roboto', sans-serif;
`;

// ====== 3D Elements ======
const RotatingTorus = () => {
  const meshRef = useRef();
  useFrame((state, delta) => {
    meshRef.current.rotation.x += delta * 0.3;
    meshRef.current.rotation.y += delta * 0.5;
  });

  return (
    <mesh ref={meshRef} position={[0, 0, -5]}>
      <torusGeometry args={[1.5, 0.5, 16, 100]} />
      <meshStandardMaterial color="#FF6B35" metalness={0.8} roughness={0.2} />
    </mesh>
  );
};

// ====== Main Section Component ======
const MainSection = () => {
  return (
    <MainSectionContainer>
      <CanvasContainer>
        <Canvas camera={{ position: [0, 0, 10], fov: 50 }}>
          <ambientLight intensity={0.5} />
          <directionalLight position={[5, 5, 5]} intensity={1} />
          <Stars radius={100} depth={50} count={5000} factor={4} fade />
          <Float speed={1.5} rotationIntensity={1} floatIntensity={2}>
            <RotatingTorus />
          </Float>
        </Canvas>
      </CanvasContainer>

      <ContentOverlay>
        <ContentWrapper>
          <SectionTitle
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Our Features
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Explore the cutting-edge technology and services that power your success.
          </SectionSubtitle>

          <FeaturesContainer>
            <FeatureCard whileHover={{ scale: 1.05 }}>
              <FeatureIcon>🚀</FeatureIcon>
              <FeatureTitle>Fast Performance</FeatureTitle>
              <FeatureDescription>
                Optimized systems that deliver speed and reliability across all devices.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard whileHover={{ scale: 1.05 }}>
              <FeatureIcon>🔒</FeatureIcon>
              <FeatureTitle>Security First</FeatureTitle>
              <FeatureDescription>
                Industry-standard encryption to keep your data safe and secure.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard whileHover={{ scale: 1.05 }}>
              <FeatureIcon>⚙️</FeatureIcon>
              <FeatureTitle>Customizable</FeatureTitle>
              <FeatureDescription>
                Flexible tools and options tailored to your business needs.
              </FeatureDescription>
            </FeatureCard>
          </FeaturesContainer>
        </ContentWrapper>
      </ContentOverlay>
    </MainSectionContainer>
  );
};

export default MainSection;
